import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Input, InputNumber, Row, Col, Select, Switch } from 'antd';

import type { IQuote } from 'models/quote';
import { MountType } from 'models/mounts';

import { useCabinetStore, useQuoteStore } from 'store';
import { calculator } from 'utils/calculator';

export const BuildQuoteButton: React.FC = () => {
    const navigate = useNavigate();
    const { quote, requestedWidth, requestedHeight, setRequestedWidth, setRequestedHeight, setQuote, clearQuote } = useQuoteStore((state) => state);
    const { selectedCabinet, cabinets } = useCabinetStore((state) => state);
    const [form] = Form.useForm<IQuote>();
    const [open, setOpen] = useState(false);

    const onCancelClick = () => {
        setOpen(false);
        form.resetFields();
        clearQuote();
    };

    const onGenerateQuote = async () => {
        try {
            const values = await form.validateFields();
            console.log('Inputted values for the quote', values);

            const cabinet = cabinets.find((c) => c.id === values.cabinetId);

            const result = calculator({
                ourTotalCost: values.ourTotalCost, markup: values.markup, cabinet: cabinet!,
                width: values.requestedWidth, height: values.requestedHeight, discount: values.discount,
                mountType: values.mountType, includeTool: values.includeTool, includeSpares: values.includeSpares,
                includeShippingPallets: values.includeShippingPallets, items: values.items || [],
            });
            console.log('Calculated quote', result);

            const newQuote: IQuote = {
                ...values,
                result,
            };

            setQuote(newQuote);
            navigate('/quote');
        } catch (error) {
            console.error('Failed to generate quote', error);
        }
    };

    return (
        <React.Fragment>
            <Button type="primary" onClick={() => setOpen(true)}>Generate Quote</Button>

            <Modal
                title="Generate Quote"
                open={open}
                okText="Generate"
                onCancel={onCancelClick}
                onOk={onGenerateQuote}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        markup: 2,
                        ...quote,
                        requestedWidth,
                        requestedHeight,
                        cabinetId: selectedCabinet?.id,
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Requestor Name" name={['requestor', 'name']} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Your Name" name={['author', 'name']} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Requested Width (ft)" name="requestedWidth" rules={[{ required: true }]}>
                                <InputNumber
                                    precision={2}
                                    min={0}
                                    step={0.1}
                                    suffix="feet"
                                    style={{ width: '100%' }}
                                    onChange={(value) => setRequestedWidth(value || 0)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Requested Height (ft)" name="requestedHeight" rules={[{ required: true }]}>
                                <InputNumber
                                    precision={2}
                                    min={0}
                                    step={0.1}
                                    suffix="feet"
                                    style={{ width: '100%' }}
                                    onChange={(value) => setRequestedHeight(value || 0)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Cabinet" name="cabinetId" rules={[{ required: true }]}>
                                <Select style={{ flex: 0.5 }}>
                                    {cabinets.map((cabinet) => (
                                        <Select.Option key={cabinet.id} value={cabinet.id}>
                                            {cabinet.name} ({cabinet.invoiceDescription})
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Markup Multiplier" name="markup" rules={[{ required: true, message: 'Required!' }]}>
                                <InputNumber
                                    precision={2}
                                    min={0}
                                    step={0.01}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Discount Percent" name={['discount', 'percent']}>
                                <InputNumber
                                    precision={2}
                                    min={0}
                                    step={0.01}
                                    suffix="%"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Discount Amount" name={['discount', 'amount']}>
                                <InputNumber
                                    precision={2}
                                    min={0}
                                    step={0.01}
                                    prefix="$"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={18}>
                            <Form.Item label="Discount Name" name={['discount', 'name']}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Is for Dealer" name={['isForDealer']} valuePropName="checked">
                                <Switch unCheckedChildren="No" checkedChildren="Yes" />
                            </Form.Item>
                        </Col>
                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.isForDealer !== currentValues.isForDealer}>
                            {({ getFieldValue }) => {
                                return getFieldValue('isForDealer') ? (
                                    <Col span={24}>
                                        <Form.Item label="Total Cost" name={['ourTotalCost']} rules={[{ required: true, message: 'When working with a dealer, we require the full cost to us.'}]}>
                                            <InputNumber
                                                prefix="$"
                                                precision={2}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                ) : null;
                            }}
                        </Form.Item>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="mountType"
                                label="Mount Type"
                                extra="The type of mounting that will be purchased. Custom will not have a price included."
                                rules={[{ required: true, message: 'Required!' }]}
                            >
                                <Select<MountType>>
                                    <Select.Option value={MountType.Wall}>Wall</Select.Option>
                                    <Select.Option value={MountType.Flybars}>Flybars</Select.Option>
                                    <Select.Option value={MountType.GroundStack}>Ground Stacked</Select.Option>
                                    <Select.Option value={MountType.Custom}>Custom</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label="Include Tool" name="includeTool" valuePropName="checked">
                                <Switch unCheckedChildren="No" checkedChildren="Yes" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label="Include Spares" name="includeSpares" valuePropName="checked">
                                <Switch unCheckedChildren="No" checkedChildren="Yes" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label="Include Shipping Pallets" name="includeShippingPallets" valuePropName="checked">
                                <Switch unCheckedChildren="No" checkedChildren="Yes" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Additional Invoice Line Items">
                        <Form.List name="items">
                            {(fields, { add, remove }) => (
                                <React.Fragment>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row key={key} gutter={16}>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...restField}
                                                    label="Quantity"
                                                    name={[name, 'quantity']}
                                                    rules={[{ required: true, message: 'Missing quantity' }]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        step={1}
                                                        precision={0}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={10}>
                                                <Form.Item
                                                    {...restField}
                                                    label="Description"
                                                    name={[name, 'description']}
                                                    rules={[{ required: true, message: 'Missing description' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={7}>
                                                <Form.Item
                                                    {...restField}
                                                    label="Cost"
                                                    name={[name, 'unitPrice']}
                                                    rules={[{ required: true, message: 'Missing cost' }]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        step={0.01}
                                                        precision={2}
                                                        prefix="$"
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={1}>
                                                <Form.Item label=" ">
                                                    <Button type="primary" danger size="small" onClick={() => remove(name)}>X</Button>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block>Add Item</Button>
                                    </Form.Item>
                                </React.Fragment>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Modal>
        </React.Fragment>
    );
};
